import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useSentryUser } from '../hooks/useSentry';
import { clearScreenCache } from '../utils/screenCache';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  // Mantido no-op por compatibilidade de API: o logout automático por
  // inatividade foi desativado a pedido do usuário (a sessão não deve cair
  // sozinha enquanto a aba estiver aberta, mesmo sem interação — como no
  // Slack). Só encerra por ação manual (signOut) ou por o token do Supabase
  // realmente expirar/ser revogado.
  resetSessionTimeout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Integração com Sentry para tracking de usuário
  useSentryUser(user ? {
    id: user.id,
    email: user.email,
    name: user.user_metadata?.name
  } : null);

  const resetSessionTimeout = useCallback(() => {}, []);

  // Outra pessoa entrou (ou saiu) nesta aba: nada da sessão anterior pode aparecer
  useEffect(() => {
    clearScreenCache();
  }, [user?.id]);

  useEffect(() => {
    // Verificar sessão atual
    const checkSession = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('Erro ao verificar sessão:', sessionError);
          setLoading(false);
          return;
        }

        if (session?.user) {
          console.log('Sessão existente encontrada:', session.user);
          setUser(session.user);
        }

        setLoading(false);
      } catch (err) {
        console.error('Erro ao verificar sessão:', err);
        setLoading(false);
      }
    };

    checkSession();

    // Listen for changes on auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // O Supabase reemite o usuário ao renovar o token e ao voltar para a aba.
      // Trocar o objeto por um igual fazia toda tela com [user] nas dependências
      // buscar tudo de novo; só troca quando é outra pessoa ou o cadastro mudou.
      const next = session?.user ?? null;
      setUser(prev =>
        prev && next && prev.id === next.id && prev.updated_at === next.updated_at ? prev : next
      );
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const value = useMemo(
    () => ({ user, loading, signOut, resetSessionTimeout }),
    [user, loading, signOut, resetSessionTimeout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};